// The ContentIQ project rail as a four-phase accordion (design settled with
// Edward 2026-10-08): 1 Gathering → 2 Content → 3 Page build → 4 Approve &
// launch. Every project shows the SAME phases and the same steps — a skipped
// step is greyed with a badge, never hidden (Edward's explicit rule). Phases
// collapse to a one-line outcome summary ("done — answers saved", "package:
// 3 of 5 back"); the open phase is where you are, and any phase expands on a
// header click. Every phase has sub-steps (round 9: Page build and Approve &
// launch expand into their live journey too — Edward, 2026-10-09).
//
// Purely presentational — the wizard computes phase state/summaries/steps
// and owns navigation. Colors from wizardTheme only.
import React from 'react';
import { FONTS, INK, NEUTRAL, HUES, RADII, STATE } from '../utils/wizardTheme';

// Holder tones match CiqBallChips: your move amber (gate), build team blue,
// client purple.
const BADGE_TONES = {
  gate: { color: STATE.gate.main, background: STATE.gate.tint },
  builder: { color: HUES.strategy.text, background: HUES.strategy.tint },
  client: { color: HUES.scripts.text, background: HUES.scripts.tint },
  done: { color: STATE.done.main, background: STATE.done.tint },
  muted: { color: INK.muted, background: NEUTRAL.band },
};

function StepBadge({ badge }) {
  if (!badge) return null;
  return (
    <span style={{
      ...BADGE_TONES[badge.tone || 'muted'], borderRadius: RADII.pill,
      padding: '1px 8px', fontSize: '10px', fontWeight: 700, whiteSpace: 'nowrap',
    }}>
      {badge.label}
    </span>
  );
}

const DOT = {
  done: STATE.done.main,
  current: HUES.strategy.main,
  todo: NEUTRAL.controlSoft,
};

function StepRow({ step, onStepClick }) {
  const isCurrent = step.state === 'current';
  return (
    <div
      onClick={step.clickable ? () => onStepClick(step.key) : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: '7px', paddingLeft: '10px',
        fontSize: '12.5px', lineHeight: 1.45,
        color: isCurrent ? INK.primary : step.dimmed ? INK.muted : INK.secondary,
        fontWeight: isCurrent ? 700 : 400,
        cursor: step.clickable ? 'pointer' : 'default',
      }}>
      <span style={{
        width: '8px', height: '8px', borderRadius: RADII.pill, flexShrink: 0,
        background: DOT[step.state] || DOT.todo,
        border: isCurrent ? `2px solid ${HUES.strategy.tint}` : 'none',
        boxSizing: 'content-box',
      }} />
      <span>{step.label}</span>
      <StepBadge badge={step.badge} />
    </div>
  );
}

const HEADER_STATE = {
  done: { label: STATE.done.text, summary: INK.secondary },
  current: { label: HUES.strategy.deep, summary: INK.body },
  todo: { label: INK.secondary, summary: INK.muted },
};

export default function CiqPhaseRail({ phases, onStepClick }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontFamily: FONTS.body }}>
      {phases.map(ph => {
        const colors = HEADER_STATE[ph.state] || HEADER_STATE.todo;
        const open = !!ph.open;
        return (
          <div key={ph.key} style={{
            borderRadius: RADII.small,
            border: `1px solid ${ph.state === 'current' ? HUES.strategy.border : 'transparent'}`,
            background: ph.state === 'current' ? HUES.strategy.tint : NEUTRAL.band,
            padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: '7px',
          }}>
            <div
              onClick={ph.onHeaderClick}
              title={ph.steps && ph.steps.length ? (open ? 'Collapse' : 'Expand') : undefined}
              style={{ display: 'flex', alignItems: 'baseline', gap: '6px', cursor: ph.onHeaderClick ? 'pointer' : 'default', fontSize: '12px' }}>
              <span style={{ color: colors.label, fontWeight: 700, whiteSpace: 'nowrap' }}>
                {ph.number} · <b>{ph.label}</b>
              </span>
              {ph.steps && ph.steps.length > 0 && (
                <span style={{ color: INK.muted, fontSize: '10px' }}>{open ? '▾' : '▸'}</span>
              )}
            </div>
            {ph.summary && (!open || !ph.steps || !ph.steps.length) && (
              <div style={{ fontSize: '11.5px', color: colors.summary, lineHeight: 1.45, paddingLeft: '2px', whiteSpace: 'pre-line' }}>
                {ph.summary}
              </div>
            )}
            {open && (ph.steps || []).map(stp => (
              <StepRow key={stp.key} step={stp} onStepClick={onStepClick} />
            ))}
          </div>
        );
      })}
    </div>
  );
}
